import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Edit2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from "@/components/ui/PaginationWrapper";
import { userService } from '../../_services';

function ManageGroup({ 
    titleText,
    whName,
    wholesalerId,
    activePagin,
    openWholesalerPopup,
    dataId,
    wholesalerName,
    closeManageGroupPopup,
    openGroupPopup,
    whIsRule,
    whSearch,
    backActivePagin
}) {
    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [groupActivePagin, setGroupActivePagin] = useState(1);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [dataNotFound, setDataNotFound] = useState('');
    const [searchText, setSearchText] = useState('');
    const [activeState, setActiveState] = useState(false);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = (response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    };

    const fetchDataLocally = useCallback(async (roleName, roleType, id) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleName, roleType, id })
            };
            
            const response = await fetch(`/api/admin/GetGroupList`, requestOptions);
            const apidata = await handleResponse(response);
            
            if (apidata.length <= 12) {
                setUserList(apidata);
                setMainAPIResult(apidata);
                setUserListTemp(apidata);
            } else {
                let userlistobj = JSON.parse(JSON.stringify(apidata));
                let usertemplistobj = userlistobj.length > 12 ? userlistobj.splice(0, 12) : userlistobj;
                setUserList(apidata);
                setMainAPIResult(apidata);
                setUserListTemp(usertemplistobj);
            }
            setLoading(false);
            
            if (backActivePagin > 1) {
                handlePageChange(backActivePagin);
            }
        } catch (error) {
            setLoading(false);
        }
    }, [backActivePagin]);

    useEffect(() => {
        if (userdata) {
            fetchDataLocally(userdata.paramsname, userdata.userrole, dataId);
        }
    }, [userdata, dataId, fetchDataLocally]);

    const handlePageChange = useCallback((pageNumber) => {
        if (backActivePagin > 1 && !activeState) {
            const updated = mainAPIResult.map(a => ({ ...a, isineditedmode: false }));
            let userlistobj = JSON.parse(JSON.stringify(userList));
            let usertemplistobj = userlistobj.length > 12 
                ? userlistobj.splice((pageNumber - 1) * 12, 12) 
                : userlistobj;
            
            setUserList(updated);
            setMainAPIResult(updated);
            setActivePage(pageNumber);
            setUserListTemp(usertemplistobj);
            setGroupActivePagin(pageNumber);
            setActiveState(true);
            setError('');
        } else {
            const updated = mainAPIResult.map(a => ({ ...a, isineditedmode: false }));
            let userlistobj = JSON.parse(JSON.stringify(userList));
            let usertemplistobj = userlistobj.length > 12 
                ? userlistobj.splice((pageNumber - 1) * 12, 12) 
                : userlistobj;
            
            setUserList(updated);
            setMainAPIResult(updated);
            setActivePage(pageNumber);
            setUserListTemp(usertemplistobj);
            setGroupActivePagin(pageNumber);
            setError('');
        }
    }, [backActivePagin, activeState, mainAPIResult, userList]);

    const handleSearch = useCallback(() => {
        let userlistobj = JSON.parse(JSON.stringify(mainAPIResult)).filter(e1 =>
            e1.groupNumber.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
        );
        let copyuserlistobj = JSON.parse(JSON.stringify(mainAPIResult)).filter(e1 =>
            e1.groupNumber.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
        );

        if (userlistobj.length === 0 || copyuserlistobj.length === 0) {
            setDataNotFound('No records found');
        } else {
            setDataNotFound('');
        }

        let usertemplistobj = userlistobj.length > 12 ? userlistobj.splice(0, 12) : userlistobj;
        setActivePage(1);
        setUserList(copyuserlistobj);
        setUserListTemp(usertemplistobj);
    }, [mainAPIResult, searchText]);

    const editGroup = useCallback((groupNumber) => {
        const updated = mainAPIResult.map(a => ({
            ...a,
            isineditedmode: a.groupNumber === groupNumber
        }));
        
        if (updated.length <= 12) {
            setUserList(updated);
            setMainAPIResult(updated);
            setUserListTemp(updated);
        } else {
            let userlistobj = JSON.parse(JSON.stringify(updated));
            let usertemplistobj = userlistobj.length > 12 ? userlistobj.splice(0, 12) : userlistobj;
            setUserList(updated);
            setMainAPIResult(updated);
            setUserListTemp(usertemplistobj);
        }
        setError('');
        handleSearch();
    }, [mainAPIResult, handleSearch]);

    const handleCancel = useCallback((groupNumber) => {
        const updated = mainAPIResult.map(a => ({
            ...a,
            isineditedmode: a.groupNumber === groupNumber ? false : a.isineditedmode
        }));
        
        if (updated.length <= 12) {
            setUserList(updated);
            setMainAPIResult(updated);
            setUserListTemp(updated);
        } else {
            let userlistobj = JSON.parse(JSON.stringify(updated));
            let usertemplistobj = userlistobj.length > 12 ? userlistobj.splice(0, 12) : userlistobj;
            setUserList(updated);
            setMainAPIResult(updated);
            setUserListTemp(usertemplistobj);
        }
        setError('');
        handleSearch();
    }, [mainAPIResult, handleSearch]);

    const updateGroupData = useCallback(async (roleName, roleType, WhName, GN, WhComm, updatedCommission) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleName, roleType, WhName, GN, WhComm })
            };
            
            const response = await fetch(`/api/admin/Update_GN`, requestOptions);
            const apidata = await handleResponse(response);
            
            if (apidata.length > 0) {
                if (apidata[0].code == "1") {
                    const updated = mainAPIResult.map(a => {
                        if (a.groupNumber === GN) {
                            return {
                                ...a,
                                commission: WhComm,
                                isineditedmode: false
                            };
                        }
                        return { ...a, isineditedmode: false };
                    });
                    
                    setMainAPIResult(updated);
                    setUserList(updated);
                    
                    if (updated.length <= 12) {
                        setUserListTemp(updated);
                    } else {
                        let userlistobj = JSON.parse(JSON.stringify(updated));
                        let usertemplistobj = userlistobj.length > 12 
                            ? userlistobj.splice((activePage - 1) * 12, 12) 
                            : userlistobj;
                        setUserListTemp(usertemplistobj);
                    }
                    
                    setError('');
                    setSuccessMessage(apidata[0].statusMessage);
                    setTimeout(() => setSuccessMessage(''), 3000);
                } else {
                    setSuccessMessage('');
                    setError(apidata[0].statusMessage);
                }
            }
        } catch (error) {
            setSuccessMessage('');
            setError('Error updating data');
        }
    }, [mainAPIResult, activePage]);

    const saveUpdates = useCallback((item, updatedCommission) => {
        const commission = updatedCommission.replace('$', '');
        const pattern = /^[A-Za-z]+$/;
        
        if (!commission || commission === null) {
            setError('Commission cannot be blank');
        } else if (pattern.test(commission)) {
            setError('Commission must be numeric e.g. 1.5');
        } else if (commission.indexOf('$') > -1 || commission.indexOf(' ') > -1) {
            setError('Commission must be numeric e.g. 1.5');
        } else if (commission < 0 || commission == 0) {
            setError('Commission should be greater than 0');
        } else if (commission > 10) {
            setError('Commission should not be more than 10');
        } else if (!userService.validateCurrency(commission)) {
            setError('Commission must be numeric e.g. 1.5');
        } else {
            updateGroupData(
                userdata.paramsname,
                userdata.userrole,
                dataId,
                item.groupNumber,
                '$' + parseFloat(commission, 10).toFixed(2).toString(),
                commission
            );
        }
    }, [userdata, dataId, updateGroupData]);

    const cancelPopup = () => {
        closeManageGroupPopup(wholesalerId, wholesalerName, activePagin, groupActivePagin, whIsRule, whSearch);
        openWholesalerPopup(activePagin, whSearch);
    };

    const handleOpenGroup = () => {
        closeManageGroupPopup(wholesalerId, wholesalerName, activePagin, groupActivePagin, whIsRule, whSearch);
        openGroupPopup(wholesalerId, wholesalerName, activePagin, groupActivePagin, whIsRule, whSearch);
    };

    return (
        <Dialog open={true} onOpenChange={cancelPopup}>
            <DialogContent className="max-w-4xl max-h-[90vh] [&>div]:!p-0">
                <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6">
                    <DialogTitle>Group Numbers: {whName}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        <div className="flex gap-4 px-4 sm:px-6">
                            <Input
                                type="text"
                                placeholder="Search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyUp={handleSearch}
                                className="flex-1"
                            />
                            <Button 
                                onClick={handleOpenGroup}
                                className="bg-primary hover:bg-primary/90"
                            >
                                Create New Group
                            </Button>
                        </div>

                        {userList && userList.length > 0 ? (
                            <div className="px-4 sm:px-6 pb-4 sm:pb-6 mt-4">
                                <div className="overflow-auto max-h-[60vh] space-y-4">
                                    <div className="border rounded-lg overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Group Number</TableHead>
                                                    <TableHead>Commission</TableHead>
                                                    <TableHead className="text-center">Action</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {userListTemp.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell className="font-medium">
                                                            {item.groupNumber}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.isineditedmode ? (
                                                                <Input
                                                                    type="text"
                                                                    name="commission"
                                                                    defaultValue={item.commission}
                                                                    id={`commission-${index}`}
                                                                    className="max-w-[150px]"
                                                                />
                                                            ) : (
                                                                item.commission
                                                            )}
                                                        </TableCell>
                                                    <TableCell className="text-center">
                                                        {item.isineditedmode ? (
                                                            <div className="flex gap-2 justify-center">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        const commissionInput = document.getElementById(`commission-${index}`);
                                                                        saveUpdates(item, commissionInput.value);
                                                                    }}
                                                                    className="bg-primary hover:bg-primary/90"
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={() => handleCancel(item.groupNumber)}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            whIsRule !== "true" && (
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => editGroup(item.groupNumber)}
                                                                            >
                                                                                <Edit2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Edit</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            )
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            </TableBody>
                                        </Table>
                                    </div>

                                    {userList.length > 12 && (
                                        <div className="flex justify-center mt-4">
                                            <Pagination
                                                activePage={activePage}
                                                itemsCountPerPage={12}
                                                totalItemsCount={userList.length}
                                                pageRangeDisplayed={5}
                                                onChange={handlePageChange}
                                            />
                                        </div>
                                    )}
                                </div>
                                {successMessage && (
                                    <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md mt-2 border border-green-200">
                                        {successMessage}
                                    </div>
                                )}
                                
                                {error && (
                                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-2">
                                        {error}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground px-4 sm:px-6">
                                <h3>{dataNotFound || 'No records found'}</h3>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ManageGroup;
